import { Domain, DynamicPageVariant, Website } from '@betterfunnel/types';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { db, functions } from '../Firebase';
import { getAnalyticsByDates } from '../services/AnalyticsService';
import { dynamicPageVariantsColRef } from '../services/DynamicPageVariantsService';
import { analyticsRangeToDates, emptyConverter, isJwtExpired } from '../utils';
import { DashboardContext } from './DashboardContext';
import { UserContext } from './UserContext';

const tokenFetchingPromises: Record<string, Promise<string> | undefined> = {};

interface DynamicPageContextProps {
  websites?: QueryDocumentSnapshot<Website>[];
  variants?: QueryDocumentSnapshot<DynamicPageVariant>[];
  customRangeAnalytics?: any;
  hasChangesToDeploy?: boolean;
  primaryDomain?: QueryDocumentSnapshot<Domain>;
  relevantDomains?: QueryDocumentSnapshot<Domain>[];

  dynamicPageWebsitePreviewToken?: string;
  fetchDynamicPageWebsitePreviewToken: () => Promise<string>;
}

export const DynamicPageContext = createContext<DynamicPageContextProps>({
  fetchDynamicPageWebsitePreviewToken: async () => '',
});

export function DynamicPageContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, analyticsRange, domains } =
    useContext(DashboardContext);

  const [dynamicPageWebsitePreviewToken, setDynamicPageWebsitePreviewToken] =
    useState<string>();
  const [websites, setWebsites] = useState<QueryDocumentSnapshot<Website>[]>();
  const [customRangeAnalytics, setCustomRangeAnalytics] = useState<any>();
  const [variants, setVariants] =
    useState<QueryDocumentSnapshot<DynamicPageVariant>[]>();

  const relevantDomains = useMemo(() => {
    if (!domains) return domains;

    if (selectedDynamicPage?.data().domains) {
      return domains.filter((d) =>
        selectedDynamicPage?.data().domains?.includes(d.data().domain),
      );
    }

    return domains;
  }, [domains, selectedDynamicPage]);

  const hasChangesToDeploy = useMemo(() => {
    return selectedDynamicPage?.data().hasChangesToPublish;
  }, [selectedDynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;

    return (
      relevantDomains.find((d) => d.data().type === 'custom') ||
      relevantDomains[0]
    );
  }, [relevantDomains]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setWebsites(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const websitesColRef = collection(
      db,
      'accounts',
      accountId,
      'campaigns',
      dynamicPageId,
      'websites',
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(
      query(
        websitesColRef,
        where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
        orderBy('createdAt', 'asc'),
      ),
      (websites) => {
        setWebsites(websites.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setVariants(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const variantsColRef = dynamicPageVariantsColRef(accountId, dynamicPageId);
    const unsubscribe = onSnapshot(
      query(
        variantsColRef,
        where('status', 'in', ['draft', 'active', 'disabled']),
        orderBy('createdAt', 'asc'),
      ),
      (variants) => {
        setVariants(variants.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  const fetchDynamicPageWebsitePreviewToken = useCallback(async () => {
    const key = `customPreviewToken-${selectedAccount?.id}-${selectedDynamicPage?.id}`;
    const currentToken = localStorage.getItem(key);

    if (currentToken && !isJwtExpired(currentToken)) {
      setDynamicPageWebsitePreviewToken(currentToken);
      return currentToken;
    }

    if (tokenFetchingPromises[key]) {
      return tokenFetchingPromises[key];
    }

    const fn = httpsCallable(
      functions,
      'accounts-campaigns-createPreviewToken',
    );

    return (tokenFetchingPromises[key] = fn({
      accountId: selectedAccount?.id,
      campaignId: selectedDynamicPage?.id,
    }).then((res) => {
      localStorage.setItem(key, res.data as string);
      setDynamicPageWebsitePreviewToken(res.data as string);

      return res.data as string;
    })).finally(() => {
      tokenFetchingPromises[key] = undefined;
    });
  }, [selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    setCustomRangeAnalytics(undefined);

    if (!selectedAccount || !selectedDynamicPage) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    let relevant = true;
    getAnalyticsByDates(
      selectedAccount.id,
      'campaign',
      selectedDynamicPage.id,
      startDate,
      endDate,
    ).then((res) => {
      if (!relevant) return;

      setCustomRangeAnalytics(res);
    });

    return () => {
      relevant = false;
    };
  }, [selectedAccount, selectedDynamicPage, analyticsRange]);

  return (
    <DynamicPageContext.Provider
      value={{
        websites,
        variants,
        customRangeAnalytics,
        hasChangesToDeploy,
        relevantDomains,
        primaryDomain,
        dynamicPageWebsitePreviewToken: dynamicPageWebsitePreviewToken,
        fetchDynamicPageWebsitePreviewToken:
          fetchDynamicPageWebsitePreviewToken,
      }}
    >
      {props.children}
    </DynamicPageContext.Provider>
  );
}

export function useDynamicPageContext() {
  return useContext(DynamicPageContext);
}
